/**
 * ChecklistDetailSections — Tab-based layout for the checklist detail sheet
 *
 * 5 tabs: Overview (description + AI snippet + notes), Evidence, Questions & Findings,
 * AI Insights, Activity. Active tab is controlled by parent so it persists across
 * item navigation.
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Loader2,
  ArrowRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  MinusCircle,
} from "lucide-react";
import { springs } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import type {
  UIChecklistItem,
  GeneratedQuestion,
  ChecklistItemAnswer,
  AuditId,
  DocumentId,
} from "@/packages/types";
import { OverviewContent } from "./ChecklistOverviewSection";
import { EvidenceContent } from "./ChecklistEvidenceSection";
import { NotesContent } from "./ChecklistNotesSection";
import { ActivityContent } from "./ChecklistActivitySection";
import {
  AISummarySection,
  AIInformationContent,
  AIExamplesContent,
} from "./ChecklistAIInfoSection";
import { QuestionsAndFindingsSection } from "./QuestionsAndFindingsSection";

// ─── Tab Types ───────────────────────────────────────────────────────────────

export const TABS = [
  "overview",
  "evidence",
  "questions",
  "ai",
  "activity",
] as const;
export type TabId = (typeof TABS)[number];

const TAB_LABELS: Record<TabId, string> = {
  overview: "Overview",
  evidence: "Evidence",
  questions: "Questions",
  ai: "AI Insights",
  activity: "Activity",
};

// ─── Questions Badge Logic ───────────────────────────────────────────────────

function getQuestionsBadge(
  questions: GeneratedQuestion[],
  answers: ChecklistItemAnswer[],
) {
  if (!questions.length) return null;
  const required = questions.filter((q) => q.required);
  const answeredOrders = new Set(answers.map((a) => a.question.order));
  const requiredAnswered = required.filter((q) => answeredOrders.has(q.order));
  const allRequiredAnswered = requiredAnswered.length === required.length;
  const noneAnswered = answeredOrders.size === 0;
  const dotColor = allRequiredAnswered
    ? "green"
    : noneAnswered && required.length > 0
      ? "red"
      : "amber";
  return { label: `${answeredOrders.size}/${questions.length}`, dotColor };
}

const DOT_COLOR_CLASSES: Record<string, string> = {
  green: "bg-green-500",
  amber: "bg-amber-500",
  red: "bg-red-500",
};

// ─── Tab Bar ─────────────────────────────────────────────────────────────────

interface TabBarProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  evidenceCount: number;
  questions: GeneratedQuestion[];
  answers: ChecklistItemAnswer[];
  findingsCount: number;
}

function TabBar({
  activeTab,
  onTabChange,
  evidenceCount,
  questions,
  answers,
  findingsCount,
}: TabBarProps) {
  const questionsBadge = getQuestionsBadge(questions, answers);
  const hasFindingsAlert = findingsCount > 0;

  return (
    <div className="flex items-center gap-0 border-b border-border">
      {TABS.map((id) => {
        const isActive = activeTab === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onTabChange(id)}
            className={`relative px-3 py-2.5 text-[12px] font-medium transition-colors cursor-pointer flex items-center gap-1.5 ${
              isActive ? "text-primary" : "text-muted hover:text-secondary"
            }`}
          >
            {TAB_LABELS[id]}

            {/* Evidence badge */}
            {id === "evidence" && evidenceCount > 0 && (
              <span className="inline-flex h-4 min-w-card items-center justify-center rounded-full bg-accent/15 px-1 text-[9px] font-semibold text-accent">
                {evidenceCount}
              </span>
            )}

            {/* Questions & Findings badge */}
            {id === "questions" && (
              <span className="inline-flex items-center gap-1">
                {questionsBadge && (
                  <>
                    <span className="inline-flex h-4 min-w-card items-center justify-center rounded-full bg-accent/15 px-1 text-[9px] font-semibold text-accent">
                      {questionsBadge.label}
                    </span>
                    <span
                      className={`size-1.5 rounded-full ${DOT_COLOR_CLASSES[questionsBadge.dotColor]}`}
                    />
                  </>
                )}
                {hasFindingsAlert && (
                  <span className="inline-flex h-4 min-w-card items-center justify-center rounded-full bg-red-500/15 px-1 text-[9px] font-semibold text-red-600">
                    {findingsCount}
                  </span>
                )}
              </span>
            )}

            {isActive && (
              <motion.div
                layoutId="checklist-tab-indicator"
                className="absolute bottom-0 left-0 right-0 h-[2px] bg-accent rounded-full"
                transition={springs.snappy}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── Compact AI Snippet Card (shown on Overview tab) ─────────────────────────

type ComplianceLevel = "compliant" | "partial" | "missing" | "non-compliant";

function getComplianceLevel(status: string): ComplianceLevel {
  const s = status.toLowerCase();
  if (s.includes("non") || s.includes("fail")) return "non-compliant";
  if (s.includes("partial")) return "partial";
  if (s.includes("missing")) return "missing";
  if (s.includes("compliant") || s.includes("pass")) return "compliant";
  return "missing";
}

const SNIPPET_STATUS_CONFIG: Record<
  ComplianceLevel,
  { icon: typeof CheckCircle2; badge: string; text: string }
> = {
  compliant: {
    icon: CheckCircle2,
    badge: "border-green-500/20 bg-green-500/5",
    text: "text-green-600",
  },
  partial: {
    icon: MinusCircle,
    badge: "border-orange-500/20 bg-orange-500/5",
    text: "text-orange-600",
  },
  missing: {
    icon: AlertTriangle,
    badge: "border-amber-500/20 bg-amber-500/5",
    text: "text-amber-600",
  },
  "non-compliant": {
    icon: XCircle,
    badge: "border-red-500/20 bg-red-500/5",
    text: "text-red-600",
  },
};

function parseSnippetStatus(
  raw: string,
): { status: string; reasoning: string } | null {
  try {
    const parsed: Record<string, unknown> = JSON.parse(raw);
    if (
      typeof parsed.complianceStatus === "string" &&
      typeof parsed.reasoning === "string"
    ) {
      return {
        status: parsed.complianceStatus,
        reasoning: parsed.reasoning,
      };
    }
    return null;
  } catch {
    return null;
  }
}

function AISnippetCard({
  item,
  onViewAI,
}: {
  item: UIChecklistItem;
  onViewAI: () => void;
}) {
  const { t } = useTranslation("audits");

  if (item.summaryStatus === "pending") {
    return (
      <div className="rounded-lg border border-accent/20 bg-accent/5 px-3 py-2.5 flex items-center gap-2">
        <Loader2 size={12} className="text-accent animate-spin shrink-0" />
        <span className="text-[12px] text-accent">
          {t("checklist.ai.generatingSummary")}
        </span>
      </div>
    );
  }

  if (!item.summaryReport) {
    return (
      <div className="rounded-lg border border-dashed border-border/60 px-3 py-2.5 flex items-center gap-2">
        <Sparkles size={13} className="text-muted shrink-0" />
        <span className="text-[12px] text-muted flex-1">
          {t("checklist.ai.noSummary")}
        </span>
        <button
          type="button"
          onClick={onViewAI}
          className="text-[11px] text-accent hover:underline shrink-0"
        >
          {t("checklist.ai.generate")}
        </button>
      </div>
    );
  }

  const structured = parseSnippetStatus(item.summaryReport);

  if (structured) {
    const level = getComplianceLevel(structured.status);
    const config = SNIPPET_STATUS_CONFIG[level];
    const Icon = config.icon;
    const MAX_CHARS = 110;
    const reasoning =
      structured.reasoning.length > MAX_CHARS
        ? `${structured.reasoning.slice(0, MAX_CHARS)}…`
        : structured.reasoning;

    return (
      <div
        className={`rounded-lg border px-3 py-2.5 space-y-1.5 ${config.badge}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Icon size={12} className={`${config.text} shrink-0`} />
            <span className={`text-[11px] font-semibold ${config.text}`}>
              {structured.status}
            </span>
          </div>
          <button
            type="button"
            onClick={onViewAI}
            className="text-[11px] text-accent hover:underline inline-flex items-center gap-0.5 shrink-0"
          >
            {t("checklist.ai.details")} <ArrowRight size={10} />
          </button>
        </div>
        <p className="text-[12px] text-secondary leading-relaxed">
          {reasoning}
        </p>
      </div>
    );
  }

  // Plain text fallback
  const MAX_CHARS = 130;
  const isTruncated = item.summaryReport.length > MAX_CHARS;
  const preview = isTruncated
    ? `${item.summaryReport.slice(0, MAX_CHARS)}…`
    : item.summaryReport;

  return (
    <div className="rounded-lg border border-accent/20 bg-accent/5 px-3 py-2.5 space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Sparkles size={12} className="text-accent shrink-0" />
        <span className="text-[11px] font-semibold text-accent">
          {t("checklist.ai.aiSummary")}
        </span>
      </div>
      <p className="text-[12px] text-secondary leading-relaxed">{preview}</p>
      <button
        type="button"
        onClick={onViewAI}
        className="text-[11px] text-accent hover:underline inline-flex items-center gap-0.5"
      >
        {isTruncated
          ? t("checklist.ai.viewFullAIInsights")
          : t("checklist.ai.viewAIInsights")}
        <ArrowRight size={10} />
      </button>
    </div>
  );
}

// ─── Tab Contents ─────────────────────────────────────────────────────────────

function OverviewTab({
  item,
  onViewAI,
}: {
  item: UIChecklistItem;
  onViewAI: () => void;
}) {
  const { t } = useTranslation("audits");

  return (
    <div className="space-y-5 pt-4">
      <OverviewContent item={item} />
      <AISnippetCard item={item} onViewAI={onViewAI} />
      <div>
        <h4 className="text-xs font-medium text-muted uppercase tracking-wider mb-2">
          {t("checklist.overview.notes")}
        </h4>
        <NotesContent item={item} />
      </div>
    </div>
  );
}

type AISubTab = "summary" | "information" | "examples";

const AI_SUB_TAB_LABELS: Record<AISubTab, string> = {
  summary: "Summary",
  information: "Information",
  examples: "Examples",
};

function AIInsightsTab({
  item,
  onGenerateSummary,
  onGenerateInformation,
  onGenerateExamples,
}: {
  item: UIChecklistItem;
  onGenerateSummary?: () => void;
  onGenerateInformation?: (instructions: string) => void;
  onGenerateExamples?: (instructions: string) => void;
}) {
  const [aiSubTab, setAiSubTab] = useState<AISubTab>("summary");

  const hasInformation = !!item.informationReport;
  const examplesCount = item.exampleDocuments?.length ?? 0;
  const infoPending = item.informationStatus === "pending";
  const examplesPending = item.exampleDocumentsStatus === "pending";

  return (
    <div className="pt-2">
      {/* Sub-tab strip */}
      <div className="flex items-center gap-0 border-b border-border mb-4">
        {(["summary", "information", "examples"] as AISubTab[]).map((id) => {
          const isActive = aiSubTab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setAiSubTab(id)}
              className={`relative px-3 py-2.5 text-[12px] font-medium transition-colors cursor-pointer flex items-center gap-1.5 ${
                isActive ? "text-primary" : "text-muted hover:text-secondary"
              }`}
            >
              {AI_SUB_TAB_LABELS[id]}

              {/* Information badge */}
              {id === "information" && (hasInformation || infoPending) && (
                <span
                  className={`size-1.5 rounded-full shrink-0 ${infoPending ? "bg-accent animate-pulse" : "bg-green-500"}`}
                />
              )}

              {/* Examples badge */}
              {id === "examples" && examplesCount > 0 && (
                <span className="inline-flex h-4 min-w-card items-center justify-center rounded-full bg-accent/15 px-1 text-[9px] font-semibold text-accent">
                  {examplesCount}
                </span>
              )}
              {id === "examples" && examplesPending && examplesCount === 0 && (
                <span className="size-1.5 rounded-full shrink-0 bg-accent animate-pulse" />
              )}

              {isActive && (
                <motion.div
                  layoutId="ai-sub-tab-indicator"
                  className="absolute bottom-0 left-0 right-0 h-[2px] bg-accent rounded-full"
                  transition={springs.snappy}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <AnimatePresence mode="wait">
        {aiSubTab === "summary" && (
          <motion.div
            key="summary"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
          >
            <AISummarySection
              item={item}
              onGenerateSummary={onGenerateSummary}
            />
          </motion.div>
        )}
        {aiSubTab === "information" && (
          <motion.div
            key="information"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
          >
            <AIInformationContent
              item={item}
              onGenerateInformation={onGenerateInformation}
            />
          </motion.div>
        )}
        {aiSubTab === "examples" && (
          <motion.div
            key="examples"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
          >
            <AIExamplesContent
              item={item}
              onGenerateExamples={onGenerateExamples}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main Export ─────────────────────────────────────────────────────────────

interface ChecklistDetailSectionsProps {
  item: UIChecklistItem;
  auditId: AuditId;
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  onGenerateSummary?: () => void;
  onGenerateInformation?: (instructions: string) => void;
  onGenerateExamples?: (instructions: string) => void;
  onFindRelatedDocuments?: () => void;
  onOpenDocument?: (docId: DocumentId) => void;
  findingsCount: number;
}

export function ChecklistDetailSections({
  item,
  auditId,
  activeTab,
  onTabChange,
  onGenerateSummary,
  onGenerateInformation,
  onGenerateExamples,
  onOpenDocument,
  findingsCount,
}: ChecklistDetailSectionsProps) {
  const evidenceCount = item.linkedDocumentIds?.length ?? 0;
  const questions = item.questions ?? [];
  const answers = item.answers ?? [];

  const tabContent: Record<TabId, ReactNode> = {
    overview: <OverviewTab item={item} onViewAI={() => onTabChange("ai")} />,
    evidence: (
      <div className="pt-4">
        <EvidenceContent
          item={item}
          auditId={auditId}
          onOpenDocument={onOpenDocument}
        />
      </div>
    ),
    ai: (
      <AIInsightsTab
        item={item}
        onGenerateSummary={onGenerateSummary}
        onGenerateInformation={onGenerateInformation}
        onGenerateExamples={onGenerateExamples}
      />
    ),
    questions: (
      <div className="pt-4">
        <QuestionsAndFindingsSection
          item={item}
          checklistItemId={item.id}
          onSwitchToEvidence={() => onTabChange("evidence")}
        />
      </div>
    ),
    activity: (
      <div className="pt-4">
        <ActivityContent checklistItemId={item.id} />
      </div>
    ),
  };

  return (
    <div>
      <TabBar
        activeTab={activeTab}
        onTabChange={onTabChange}
        evidenceCount={evidenceCount}
        questions={questions}
        answers={answers}
        findingsCount={findingsCount}
      />
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.12, ease: "easeOut" }}
        >
          {tabContent[activeTab]}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
